import { FC, useEffect, useState, type ReactNode } from 'react';

import { Monitor, Moon, Sun } from 'lucide-react';

import {
    Appearance,
    ResolvedTheme,
    ThemeService,
} from '../services/ThemeService';
import { Button } from './ui/Button';
import {
    DropdownMenu,
    DropdownMenuContent,
    DropdownMenuRadioGroup,
    DropdownMenuRadioItem,
    DropdownMenuTrigger,
} from './ui/Dropdown-Menu';

const appearanceLabels: Record<Appearance, string> = {
    light: 'Світла',
    dark: 'Темна',
    system: 'Системна',
};

const resolvedThemeIcons: Record<ResolvedTheme, ReactNode> = {
    light: <Sun className="h-4 w-4" />,
    dark: <Moon className="h-4 w-4" />,
};

const appearanceIcons: Record<Appearance, ReactNode> = {
    light: <Sun className="h-4 w-4" />,
    dark: <Moon className="h-4 w-4" />,
    system: <Monitor className="h-4 w-4" />,
};

export const AppearanceToggleDropdown: FC = () => {
    const [appearance, setAppearance] = useState<Appearance>('system');
    const [resolvedTheme, setResolvedTheme] = useState<ResolvedTheme>('light');

    useEffect(() => {
        const syncAppearance = () => {
            setAppearance(ThemeService.getAppearance());
            setResolvedTheme(ThemeService.getResolvedTheme());
            ThemeService.syncTheme();
        };

        syncAppearance();

        const mediaQuery = window.matchMedia('(prefers-color-scheme: dark)');
        mediaQuery.addEventListener('change', syncAppearance);

        return () => mediaQuery.removeEventListener('change', syncAppearance);
    }, []);

    const handleAppearanceChange = (nextAppearance: string) => {
        if (!ThemeService.isAppearance(nextAppearance)) {
            return;
        }

        ThemeService.applyAppearance(nextAppearance);
        setAppearance(nextAppearance);
        setResolvedTheme(ThemeService.getResolvedTheme());
    };

    return (
        <DropdownMenu>
            <DropdownMenuTrigger asChild>
                <Button
                    type="button"
                    variant="outline"
                    size="icon"
                    className="h-9 w-9 bg-transparent"
                    aria-label="Перемкнути тему"
                    title={`Тема: ${appearanceLabels[appearance]}`}
                >
                    {appearance === 'system'
                        ? appearanceIcons.system
                        : resolvedThemeIcons[resolvedTheme]}
                </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end" className="w-44">
                <DropdownMenuRadioGroup
                    value={appearance}
                    onValueChange={handleAppearanceChange}
                >
                    <DropdownMenuRadioItem value="light">
                        <span className="flex items-center gap-2">
                            {appearanceIcons.light}
                            Світла
                        </span>
                    </DropdownMenuRadioItem>
                    <DropdownMenuRadioItem value="dark">
                        <span className="flex items-center gap-2">
                            {appearanceIcons.dark}
                            Темна
                        </span>
                    </DropdownMenuRadioItem>
                    <DropdownMenuRadioItem value="system">
                        <span className="flex items-center gap-2">
                            {appearanceIcons.system}
                            Системна
                        </span>
                    </DropdownMenuRadioItem>
                </DropdownMenuRadioGroup>
            </DropdownMenuContent>
        </DropdownMenu>
    );
};
